import { UserStore, useUserStore } from '@/stores/userStore';
import { ErrorCode } from '@/constants/enums';
import axios from 'axios';
import router from '@/plugins/router';

const baseURL = import.meta.env.VITE_APP_API_URL;

const client = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 1000 * 60,
});

// Request interceptor to add dynamic token and handle auto refresh
client.interceptors.request.use(async (config) => {
  const userStore: UserStore = useUserStore();
  let token = userStore.userToken;
  
  //console.log('🔍 [GraphQL Client] 요청 인터셉터 실행');
  
  // Access token 만료 체크 및 자동 갱신
  if (token && userStore.isAccessTokenExpired()) {
    //console.log('🔄 [GraphQL Client] Access token 만료됨, 갱신 시도');
    const refreshSuccess = await userStore.refreshAccessToken();
    if (refreshSuccess) {
      token = userStore.userToken;
      ////console.log('✅ [GraphQL Client] 토큰 갱신 성공');
      //console.log('🔄 [GraphQL Client] 갱신된 토큰:', token);
    } else {
      //console.log('❌ [GraphQL Client] 토큰 갱신 실패');
      token = '';
    }
  }
  
  //console.log('🔍 [GraphQL Client] 요청 정보:', {
  //   url: config.url,
  //   method: config.method,
  //   baseURL: config.baseURL,
  //   fullURL: (config.baseURL || '') + (config.url || ''),
  //   headers: config.headers,
  //   data: config.data,
  //   has_token: !!token,
  //   token_length: token?.length
  // });

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    //console.log('🔍 [GraphQL Client] Authorization 헤더 추가됨');
  } else {
    //console.log('⚠️ [GraphQL Client] 토큰이 없어서 Authorization 헤더 추가 안됨');
  }
  
  return config;
}, (error) => {
  console.error('🔍 [GraphQL Client] 요청 인터셉터 에러:', error);
  return Promise.reject(error);
});

// Response interceptor to handle errors
client.interceptors.response.use(
  (response) => {
    //console.log('🔍 [GraphQL Client] 응답 인터셉터 실행 (성공)');
    //console.log('🔍 [GraphQL Client] 응답 정보:', {
    //   status: response.status,
    //   statusText: response.statusText,
    //   headers: response.headers,
    //   data: response.data,
    //   config_url: response.config?.url,
    //   config_method: response.config?.method
    // });

    // GraphQL 응답 데이터가 있는 경우 상세 로깅
    // if (response.data?.data) {
    //   //console.log('🔍 [GraphQL Client] GraphQL 응답 데이터:', JSON.stringify(response.data.data, null, 2));
    // }

    // GraphQL 에러가 있는 경우 로깅
    if (response.data?.errors) {
      console.error('⚠️ [GraphQL Client] GraphQL 에러들:', response.data.errors);
    }

    return response;
  },
  async (error) => {
    const status = error.response?.status;
    // console.error('🔍 [DEBUG] 응답 에러:', status, error.response?.statusText);
    // console.error('🔍 [DEBUG] 에러 데이터:', error.response?.data);
    
    // 401 에러 시 토큰 갱신 시도
    if (status === 401) {
      const userStore: UserStore = useUserStore();
      //console.log('🔄 [GraphQL Client] 401 에러 발생, 토큰 갱신 시도');
      
      const refreshSuccess = await userStore.refreshAccessToken();
      if (refreshSuccess) {
        ////console.log('✅ [GraphQL Client] 토큰 갱신 성공, 원래 요청 재시도');
        //console.log('🔄 [GraphQL Client] 갱신된 토큰 (401 응답):', userStore.userToken);
        // 원래 요청을 다시 시도
        const originalRequest = error.config;
        originalRequest.headers.Authorization = `Bearer ${userStore.userToken}`;
        return client(originalRequest);
      } else {
        //console.log('❌ [GraphQL Client] 토큰 갱신 실패, 로그인 페이지로 리다이렉트');
        //console.log('🔍 [GraphQL Client] 서버 재시작으로 인한 refresh token 유실 가능성');
        
        // 갱신 실패 시 로그인 페이지로 리다이렉트
        try {
          const currentPath = (router as any)?.currentRoute?.value?.path || '';
          if (currentPath !== '/login') {
            console.info('🔔 [GraphQL Client] 세션이 만료되었습니다. 다시 로그인해주세요.');
            (router as any)?.push?.('/login');
          }
        } catch (routerError) {
          console.error('🔍 [DEBUG] 라우터 에러:', routerError);
          // 라우터 실패 시 window.location 사용
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    }
    
    // HTTP 상태 코드에 따른 에러 페이지 라우팅
    if (status) {
      let errorPath = '';
      
      switch (status) {
        case 400:
          errorPath = `/${ErrorCode.BAD_REQUEST}`;
          break;
        case 401:
          errorPath = `/${ErrorCode.UNAUTHORIZED}`;
          break;
        case 403:
          errorPath = `/${ErrorCode.FORBIDDEN}`;
          break;
        case 404:
          errorPath = `/${ErrorCode.NOT_FOUND}`;
          break;
        case 500:
          errorPath = `/${ErrorCode.INTERNAL_SERVER_ERROR}`;
          break;
        default:
          // 500대 에러는 모두 500으로 처리
          if (status >= 500) {
            errorPath = `/${ErrorCode.INTERNAL_SERVER_ERROR}`;
          } else {
            // 기타 에러는 404로 처리
            errorPath = `/${ErrorCode.NOT_FOUND}`;
          }
          break;
      }
      
      // 라우터를 사용하여 에러 페이지로 이동
      try {
        const currentPath = (router as any)?.currentRoute?.value?.path || '';
        if (!currentPath.startsWith('/404') && 
            !currentPath.startsWith('/500') &&
            !currentPath.startsWith('/403') &&
            !currentPath.startsWith('/401') &&
            !currentPath.startsWith('/400')) {
          //console.log('🔍 [DEBUG] 에러 페이지로 라우팅:', errorPath);
          (router as any)?.push?.(errorPath);
        }
      } catch (routerError) {
        console.error('🔍 [DEBUG] 라우터 에러:', routerError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default client;
